/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { IUserPayment, IUserPaymentMethods, IUserSubscription } from "@/interfaces/User.interface";
import { api } from "../lib/api";
import { IUserRegistrationData, IUserLoginData, ITokens, IUserInformation, IUserResetPassword, IUserInvoice } from "@/interfaces/User.interface";
// import { tokens as Tokens } from "@/plugins/Tokens";
import { queryClient } from "@/utils/queryClient";

/**
 * Registers a user
 *
 * @param userData - user data to be sent to the server
 * @returns A Promise that resolves to a success message.
 */
export const registerUser = (userData: IUserRegistrationData): Promise<any> => {
  return api.post(`/users`, { ...userData });
};

/**
 * Patch a user
 *
 * @param userData - user data to be sent to the server
 * @returns A Promise that resolves to a success message.
 */
export const patchUser = ({ userId, userData }: { userId: string; userData: Partial<IUserRegistrationData> }): Promise<any> => {
  return api.patch(`/users/${userId}`, { ...userData }, { withAuth: true });
};

/**
 * Log user in
 *
 * @param userData - user data (email, password)
 * @returns A Promise that resolves to a success message.
 */
export const authenticateUser = (userData: IUserLoginData): Promise<ITokens> => {
  return api.post(`/tokens/access`, { ...userData }, { withAuth: true });
};

/**
 * Verify Email
 *
 * @param token - token to be verified
 * @returns A Promise that resolves to a success message.
 */
export const confirmEmail = (token: string): Promise<string> => {
  return api.post(`/tokens/verification`, { token });
};

/**
 * Request Reset Password for email
 *
 * @param email - email to reset its password
 * @returns A Promise that resolves to a success message.
 */
export const requestResetPassword = (email: string): Promise<string> => {
  return api.post(`/tokens/reset-password`, email);
};

/**
 * Reset Password
 *
 * @param token {String} - token received from backend
 * @param password {String} - password to be set
 * @returns A Promise that resolves to a success message.
 */
export const resetPassword = ({ token, data }: IUserResetPassword): Promise<string> => {
  return api.post(`/tokens/verification`, {
    token,
    data,
  });
};

/**
 * User Info
 *
 * @param userId - userId
 * @returns A Promise that resolves to a success message.
 */
export const getUserInfo = (userId: string): Promise<IUserInformation> => {
  return api.get(`/users/${userId}`, { withAuth: true });
};

/**
 * Refresh token
 *
 * @param refreshToken
 * @returns A Promise that resolves to a success message.
 */
export const refreshToken = (): Promise<ITokens> => {
  const userAuth: ITokens | undefined = queryClient.getQueryData(["user-auth"]);
  const { refresh: refreshToken } = userAuth || {};

  if (!refreshToken) {
    throw new Error("no refresh token found")
  }
  return api.post(`/tokens/refresh`, { refresh: refreshToken });
};

/**
 * User Subscriptions
 *
 * @returns A Promise that resolves to an array of IUserSubscription
 */
export const getSubscriptions = (userId: string): Promise<IUserSubscription[]> => {
  return api.get(`/users/${userId}/subscriptions`, {
    withAuth: true,
  });
};

/**
 * User Invoices
 *
 * @returns A Promise that resolves to an array of IUserInvoice
 */
export const getInvoices = (userId: string): Promise<IUserInvoice[]> => {
  return api.get(`/users/${userId}/invoices`, {
    withAuth: true,
  });
};

/**
 * User payments
 *
 * @returns A Promise that resolves to an array of IUserPayment
 */
export const getPayments = (userId: string): Promise<IUserPayment[]> => {
  return api.get(`/users/${userId}/payments`, {
    withAuth: true,
  });
};

/**
 * User payment methods
 *
 * @returns A Promise that resolves to an array of IUserPayment
 */
export const getPaymentMethods = (userId: string): Promise<IUserPaymentMethods[]> => {
  return api.get(`/users/${userId}/payment-methods`, {
    withAuth: true,
  });
};
